import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    AlertTitle,
    Chip,
    CircularProgress,
    Divider,
    Grid,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGetIdentity } from 'react-admin';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';

/**
 * Zahlungseinstellungen — seit dem Marktplatz-Rework T0 **nur lesend**.
 *
 * Zahlungen laufen über PayPal (Plattform als PayPal-Partner, Verkäufer
 * verbinden ihr eigenes PayPal-Geschäftskonto über das Verkäufer-Dashboard,
 * Plan 3.5). Mandanten pflegen hier keine Zahlungs-Secrets mehr; die
 * Stripe-Anbindung ist entfernt. Die verbleibenden Werte (Währung,
 * kostenlose Einschreibung) werden mit der Storefront (T7) dorthin verlagert.
 */
export interface PaymentSettingsData {
    id: number | null;
    companyId: number;
    paypalEnabled: boolean;
    paypalConfigured: boolean;
    paypalSandbox: boolean;
    defaultCurrency: string;
    allowFreeEnrollment: boolean;
    enabledProviders: string[];
}

const PaymentSettings: React.FC = () => {
    const { data: identity } = useGetIdentity();
    const companyId = identity?.companyId;

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [settings, setSettings] = useState<PaymentSettingsData | null>(null);

    useEffect(() => {
        if (!companyId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`${SSO_URL}/api/payment-settings/${companyId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const data = await response.json();
                if (!cancelled) {
                    setSettings(data.settings ?? null);
                }
            } catch (error) {
                console.error('Failed to load payment settings:', error);
                if (!cancelled) {
                    setLoadError('Einstellungen konnten nicht geladen werden.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [companyId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon /> Zahlungseinstellungen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Übersicht der Zahlungsabwicklung für Ihren Mandanten.
            </Typography>

            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }} data-testid="paypal-platform-notice">
                <AlertTitle>Zahlungen laufen über PayPal</AlertTitle>
                Die online:academy wickelt Zahlungen als PayPal-Partner ab. Als Verkäufer auf dem Marktplatz
                verbinden Sie Ihr eigenes PayPal-Geschäftskonto im Verkäufer-Dashboard; Käufer bezahlen direkt an
                Sie, die Plattformgebühr wird im Zahlvorgang einbehalten. Eigene Zahlungs-Zugangsdaten müssen hier
                nicht mehr hinterlegt werden.
            </Alert>

            {loadError && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {loadError}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                        Aktueller Stand
                    </Typography>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<CheckCircleIcon />}
                            label="Zahlungsanbieter: PayPal (Plattformkonto)"
                            color="success"
                            variant="outlined"
                        />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                                Standard-Währung
                            </Typography>
                            <Typography>{settings?.defaultCurrency ?? 'EUR'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                                Kostenlose Einschreibungen
                            </Typography>
                            <Typography>
                                {settings?.allowFreeEnrollment ?? true ? 'Erlaubt' : 'Nicht erlaubt'}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Diese Werte sind schreibgeschützt und wandern mit der Storefront in deren Einstellungen.
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
};

export default PaymentSettings;
